import type { GetStaticProps } from 'next'
import type { FC, ReactNode } from 'react'
import type { Cart, MenuCategory, Review } from '@/types'

import Head from 'next/head'
import { useCallback, useState } from 'react'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import { HeroSection } from '@/components/landing/hero-section'
import { ToppingsMarquee } from '@/components/landing/toppings-marquee'
import { ProcessSection } from '@/components/landing/process-section'
import { MenuSection } from '@/components/landing/menu-section'
import { ReviewsSection } from '@/components/landing/reviews-section'
import { OrderSection } from '@/components/landing/order-section'
import { VisitSection } from '@/components/landing/visit-section'
import { FaqSection } from '@/components/landing/faq-section'
import { OrderBar } from '@/components/landing/order-bar'
import { getMenu, getReviews } from '@/lib/data/menu'
import { getHoursSummary } from '@/lib/hours'
import { MAX_QUANTITY_PER_ITEM } from '@/constants/order'
import { siteConfig } from '@/constants/site'

interface HomeProps {
    categories: Array<MenuCategory>
    reviews: Array<Review>
}

const restaurantSchema = {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    'name': siteConfig.name,
    'description': siteConfig.description,
    'servesCuisine': ['Congee', 'Asian comfort food'],
    'priceRange': '$',
    'telephone': siteConfig.phoneHref.replace('tel:', ''),
    'acceptsReservations': false,
    'address': {
        '@type': 'PostalAddress',
        'streetAddress': siteConfig.address.street,
        'addressLocality': siteConfig.address.city,
        'addressRegion': siteConfig.address.region,
        'postalCode': siteConfig.address.postalCode,
        'addressCountry': siteConfig.address.country
    },
    'openingHoursSpecification': getHoursSummary()
        .filter(group => group.open && group.close)
        .map(group => ({
            '@type': 'OpeningHoursSpecification',
            'dayOfWeek': group.days,
            'opens': group.open,
            'closes': group.close
        }))
}

const restaurantSchemaJson = JSON.stringify(restaurantSchema).replace(/</g, '\\u003c')

const Home: FC<HomeProps> = ({ categories, reviews }): ReactNode => {
    const [cart, setCart] = useState<Cart>({})

    const handleAdd = useCallback((itemId: string) => {
        setCart(current => ({
            ...current,
            [itemId]: Math.min(MAX_QUANTITY_PER_ITEM, (current[itemId] ?? 0) + 1)
        }))
    }, [])

    const handleQuantityChange = useCallback((itemId: string, quantity: number) => {
        setCart(current => {
            const next = { ...current }

            if (quantity <= 0) delete next[itemId]
            else next[itemId] = Math.min(MAX_QUANTITY_PER_ITEM, quantity)

            return next
        })
    }, [])

    const handleClear = useCallback(() => setCart({}), [])

    return (
        <Layout>
            <Seo />

            <Head>
                <script
                    type='application/ld+json'
                    dangerouslySetInnerHTML={{ __html: restaurantSchemaJson }}
                />
            </Head>

            <HeroSection />
            <ToppingsMarquee />
            <ProcessSection />

            <MenuSection
                categories={categories}
                cart={cart}
                onAdd={handleAdd}
            />

            <ReviewsSection reviews={reviews} />

            <OrderSection
                categories={categories}
                cart={cart}
                onQuantityChange={handleQuantityChange}
                onClear={handleClear}
            />

            <VisitSection />
            <FaqSection />

            <OrderBar
                categories={categories}
                cart={cart}
            />
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<HomeProps> = async () => {
    const [menuResult, reviewsResult] = await Promise.allSettled([getMenu(), getReviews()])

    if (menuResult.status === 'rejected') {
        console.error('Could not load the menu:', menuResult.reason instanceof Error ? menuResult.reason.message : menuResult.reason)
    }

    if (reviewsResult.status === 'rejected') {
        console.error('Could not load the reviews:', reviewsResult.reason instanceof Error ? reviewsResult.reason.message : reviewsResult.reason)
    }

    const hasFailure = menuResult.status === 'rejected' || reviewsResult.status === 'rejected'

    return {
        props: {
            categories: menuResult.status === 'fulfilled' ? menuResult.value : [],
            reviews: reviewsResult.status === 'fulfilled' ? reviewsResult.value : []
        },
        revalidate: hasFailure ? 30 : 60
    }
}

export default Home
